import React, { Component } from "react";
import axios from "axios";
import { Table, Button, Modal, Form, Row, Col, Badge } from "react-bootstrap";
import CartContext from "./CartContext";

export default class ProjectRisks extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      risks: [],
      projects: [],
      employees: [],
      showModal: false,
      currentRisk: this.getEmptyRisk(),
      page: 1,
      pageSize: 5,
      filteredRisks: [],
      filterProject: "",
      filterSeverity: "",
      filterAssignedTo: "",
      filterStatus: "",
    };
  }

  getEmptyRisk = () => ({
    id: null,
    projectId: "",
    riskTitle: "",
    riskDetails: "",
    severity: "",
    recommendedAction: "",
    assignedTo: "",
    targetDate: "",
    status: 0,
  });

  componentDidMount() {
    this.loadData();
  }

  loadData = () => {
    const api = process.env.REACT_APP_API;
    const { storeId } = this.context;
    axios.get(`${api}ProjectRisk/Store/${storeId}`).then((res) => {
      this.setState({ risks: res.data, filteredRisks: res.data });
    });
    axios
      .get(`${api}Project/Store/${storeId}`)
      .then((res) => this.setState({ projects: res.data }));
    axios
      .get(`${api}Employee/GetEmployees/${storeId}`)
      .then((res) => this.setState({ employees: res.data }));
  };

  handleShow = (risk = null) => {
    this.setState({
      showModal: true,
      currentRisk: risk ? { ...risk } : this.getEmptyRisk(),
    });
  };

  handleClose = () => {
    this.setState({ showModal: false, currentRisk: this.getEmptyRisk() });
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      currentRisk: { ...prev.currentRisk, [name]: value },
    }));
  };

  handleSave = () => {
    const { currentRisk } = this.state;
    const api = process.env.REACT_APP_API;

    if (!currentRisk.status) {
      alert("Please select Status");
      return;
    }

    // --- Validations ---
    if (!currentRisk.riskTitle?.trim()) {
      alert("Risk Title is required");
      return;
    }
    if (!currentRisk.riskDetails?.trim()) {
      alert("Risk Details are required");
      return;
    }
    if (!currentRisk.projectId) {
      alert("Please select a Project");
      return;
    }
    if (!currentRisk.severity) {
      alert("Please select Severity");
      return;
    }

    // --- Prepare payload ---
    const payload = {
      ...currentRisk,
      targetDate: currentRisk.targetDate
        ? new Date(currentRisk.targetDate).toISOString()
        : null,
    };
    if (!payload.assignedTo) {
      payload.assignedTo = null;
    }

    if (payload.id) {
      // Update existing
      axios
        .put(`${api}ProjectRisk/${payload.id}`, payload)
        .then(() => {
          this.loadData();
          this.handleClose();
        })
        .catch((err) => {
          console.error("Update failed:", err);
          alert("Failed to update risk");
        });
    } else {
      // New record
      payload.id = "00000000-0000-0000-0000-000000000000"; // ✅ important
      payload.storeId = this.context.storeId;
      //   payload.createdBy = this.context.userId;
      payload.created = new Date().toISOString();

      // Map severity string -> int
      const severityMap = { Low: 1, Moderate: 2, High: 3, Critical: 4 };
      payload.severity = severityMap[currentRisk.severity] || null;

      // Format date properly
      if (payload.targetDate) {
        payload.targetDate = new Date(payload.targetDate)
          .toISOString()
          .split("T")[0];
      }

      axios
        .post(`${api}ProjectRisk`, payload)
        .then(() => {
          this.loadData();
          this.handleClose();
        })
        .catch((err) => {
          console.error("Save failed:", err.response?.data || err.message);
          alert(
            "Failed to save risk: " + (err.response?.data || "Unknown error")
          );
        });
    }
  };

  handleDelete = (id) => {
    if (window.confirm("Are you sure to delete this risk?")) {
      const api = process.env.REACT_APP_API;
      axios.delete(`${api}ProjectRisk/${id}`).then(this.loadData);
    }
  };

  formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-IN");
  };

  getProjectName = (projectId) => {
    const project = this.state.projects.find((p) => p.id === projectId);
    return project ? project.projectName : "—";
  };

  getEmployeeName = (empId) => {
    const emp = this.state.employees.find((e) => e.id === empId);
    return emp ? emp.firstName + " " + emp.lastName : "—";
  };

  applyFilters = () => {
    const {
      risks,
      filterProject,
      filterSeverity,
      filterAssignedTo,
      filterStatus,
    } = this.state;

    let filtered = risks;

    if (filterProject) {
      filtered = filtered.filter((r) => r.projectId === filterProject);
    }
    if (filterSeverity) {
      filtered = filtered.filter((r) => String(r.severity) === filterSeverity);
    }
    if (filterAssignedTo) {
      filtered = filtered.filter((r) => r.assignedTo === filterAssignedTo);
    }
    if (filterStatus) {
      filtered = filtered.filter((r) => String(r.status) === filterStatus);
    }

    this.setState({ filteredRisks: filtered });
  };

  getStatusText = (status) => {
    switch (status) {
      case 1:
        return { text: "Open", color: "warning" };
      case 2:
        return { text: "In Progress", color: "info" };
      case 3:
        return { text: "Closed", color: "success" };
      default:
        return { text: "Unknown", color: "secondary" };
    }
  };

  getSeverityText = (value) => {
    switch (value) {
      case 1:
        return "Low";
      case 2:
        return "Moderate";
      case 3:
        return "High";
      case 4:
        return "Critical";
      default:
        return "-";
    }
  };

  render() {
    const { risks, showModal, currentRisk, page, pageSize } = this.state;
    const startIndex = (page - 1) * pageSize;
    const pagedData = this.state.filteredRisks.slice(
      startIndex,
      startIndex + pageSize
    );

    return (
      <div className="container mt-1">
        <h3 className="shadow">
          <b>Project Risks Management</b>
        </h3>
        <Row className="mb-3 mt-3">
          <Col>
            <Form.Select
              value={this.state.filterProject}
              onChange={(e) =>
                this.setState(
                  { filterProject: e.target.value },
                  this.applyFilters
                )
              }
            >
              <option value="">All Projects</option>
              {this.state.projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.projectName}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col>
            <Form.Select
              value={this.state.filterSeverity}
              onChange={(e) =>
                this.setState(
                  { filterSeverity: e.target.value },
                  this.applyFilters
                )
              }
            >
              <option value="">All Severities</option>
              <option value="1">Low</option>
              <option value="2">Moderate</option>
              <option value="3">High</option>
              <option value="4">Critical</option>
            </Form.Select>
          </Col>
          <Col>
            <Form.Select
              value={this.state.filterAssignedTo}
              onChange={(e) =>
                this.setState(
                  { filterAssignedTo: e.target.value },
                  this.applyFilters
                )
              }
            >
              <option value="">All Employees</option>
              {this.state.employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col>
            <Form.Select
              value={this.state.filterStatus}
              onChange={(e) =>
                this.setState(
                  { filterStatus: e.target.value },
                  this.applyFilters
                )
              }
            >
              <option value="">All Status</option>
              <option value="1">Open</option>
              <option value="2">In Progress</option>
              <option value="3">Closed</option>
            </Form.Select>
          </Col>
        </Row>

        <Button variant="primary" onClick={() => this.handleShow()}>
          Add Risk
        </Button>
        <Table striped bordered hover className="mt-3">
          <thead>
            <tr>
              <th>Project</th>
              <th>Title</th>
              <th>Severity</th>
              <th>Assigned To</th>
              <th>Target Date</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pagedData.map((risk) => (
              <tr key={risk.id}>
                <td>{this.getProjectName(risk.projectId)}</td>
                <td>{risk.riskTitle}</td>
                <td>{this.getSeverityText(risk.severity)}</td>
                <td>{this.getEmployeeName(risk.assignedTo)}</td>
                <td>{this.formatDate(risk.targetDate)}</td>
                <td>
                  {(() => {
                    const s = this.getStatusText(risk.status);
                    return <Badge bg={s.color}>{s.text}</Badge>;
                  })()}
                </td>
                <td>{this.formatDate(risk.created)}</td>
                <td>
                  <Button
                    size="sm"
                    variant="info"
                    onClick={() => this.handleShow(risk)}
                  >
                    Edit
                  </Button>{" "}
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => this.handleDelete(risk.id)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Pagination */}
        <div className="d-flex justify-content-between">
          <Button
            disabled={page === 1}
            onClick={() => this.setState({ page: page - 1 })}
          >
            Previous
          </Button>
          <span>
            Page {page} of {Math.ceil(risks.length / pageSize)}
          </span>
          <Button
            disabled={startIndex + pageSize >= risks.length}
            onClick={() => this.setState({ page: page + 1 })}
          >
            Next
          </Button>
        </div>

        {/* Modal */}
        <Modal show={showModal} onHide={this.handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>
              {currentRisk.id ? "Edit Project Risk" : "Add Project Risk"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              {/* Project Dropdown */}
              <Form.Group className="mb-3">
                <Form.Label>Project</Form.Label>
                <Form.Select
                  value={currentRisk.projectId || ""}
                  onChange={(e) =>
                    this.setState({
                      currentRisk: {
                        ...currentRisk,
                        projectId: e.target.value,
                      },
                    })
                  }
                >
                  <option value="">-- Select Project --</option>
                  {this.state.projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.projectName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/* Risk Title */}
              <Form.Group className="mb-3">
                <Form.Label>Risk Title</Form.Label>
                <Form.Control
                  type="text"
                  value={currentRisk.riskTitle || ""}
                  onChange={(e) =>
                    this.setState({
                      currentRisk: {
                        ...currentRisk,
                        riskTitle: e.target.value,
                      },
                    })
                  }
                />
              </Form.Group>

              {/* Risk Details */}
              <Form.Group className="mb-3">
                <Form.Label>Risk Details</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={currentRisk.riskDetails || ""}
                  onChange={(e) =>
                    this.setState({
                      currentRisk: {
                        ...currentRisk,
                        riskDetails: e.target.value,
                      },
                    })
                  }
                />
              </Form.Group>
              {/* Assigned To Dropdown */}
              <Form.Group className="mb-3">
                <Form.Label>Assign To</Form.Label>
                <Form.Select
                  value={currentRisk.assignedTo || ""}
                  onChange={(e) =>
                    this.setState({
                      currentRisk: {
                        ...currentRisk,
                        assignedTo: e.target.value,
                      },
                    })
                  }
                >
                  <option value="">-- Select Employee --</option>
                  {this.state.employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/* Severity Dropdown */}
              <Form.Group className="mb-3">
                <Form.Label>Severity</Form.Label>
                <Form.Select
                  value={currentRisk.severity || ""}
                  onChange={(e) =>
                    this.setState({
                      currentRisk: {
                        ...currentRisk,
                        severity: Number(e.target.value),
                      },
                    })
                  }
                >
                  <option value="">-- Select Severity --</option>
                  <option value="1">Low</option>
                  <option value="2">Moderate</option>
                  <option value="3">High</option>
                  <option value="4">Critical</option>
                </Form.Select>
              </Form.Group>

              {/* Target Date */}
              <Form.Group className="mb-3">
                <Form.Label>Target Date</Form.Label>
                <Form.Control
                  type="date"
                  value={
                    currentRisk.targetDate
                      ? currentRisk.targetDate.substring(0, 10)
                      : ""
                  }
                  onChange={(e) =>
                    this.setState({
                      currentRisk: {
                        ...currentRisk,
                        targetDate: e.target.value,
                      },
                    })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={currentRisk.status || ""}
                  onChange={(e) =>
                    this.setState({
                      currentRisk: {
                        ...currentRisk,
                        status: Number(e.target.value),
                      },
                    })
                  }
                >
                  <option value="">Select Status</option>
                  <option value="1">Open</option>
                  <option value="2">In Progress</option>
                  <option value="3">Closed</option>
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.handleSave}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
